import {setItem} from '@fl/cmsch-fe-library';
import * as O from 'optics-ts';
import {none, opt} from 'ts-opt';

import {AppAction} from 'app/setup';

import {initialAdminState, AdminState} from './state';

const adminStateO = O.optic<AdminState>();

/** Reducer for the admin module state slice. */
export const adminReducer = (state: AdminState = initialAdminState, action: AppAction): AdminState => {
    switch (action.type) {
        case 'admin/GET_APP_INFO_SUCCESS': {
            const {appInfo} = action.payload;

            return O.set(adminStateO.prop('appInfo'))(opt(appInfo))(state);
        }

        case 'admin/GET_PERMISSION_DETAILS_SUCCESS':
            return O.set(adminStateO.prop('permissionDetailsPage'))(action.payload)(state);

        case 'admin/SET_EDITED_PERMISSION_ID':
            return O.set(adminStateO.prop('permissionIdInEdit'))(action.payload)(state);

        case 'admin/SET_PERMISSION_CREATE_VISIBILITY':
            return O.set(adminStateO.prop('permissionCreateVisible'))(action.payload)(state);

        case 'admin/SET_ROLE_CREATE_VISIBILITY':
            return O.set(adminStateO.prop('roleCreateModalVisible'))(action.payload)(state);

        case 'admin/SET_EDITED_ROLE_ID':
            return O.set(adminStateO.prop('roleIdInEdit'))(action.payload)(state);

        case 'admin/GET_SUBORDINATE_ASSIGNED_ROLES_SUCCESS':
            return O.set(adminStateO.prop('subordinateAssignedRolePage'))(action.payload)(state);

        case 'admin/GET_SUBORDINATE_UNASSIGNED_ROLES_SUCCESS':
            return O.set(adminStateO.prop('subordinateUnassignedRolePage'))(action.payload)(state);

        case 'admin/GET_ROLES_SUCCESS':
            return O.set(adminStateO.prop('rolesPage'))(action.payload)(state);

        case 'admin/GET_ROLE_UNASSIGNED_PERMISSIONS_SUCCESS':
            return O.set(adminStateO.prop('roleUnassignedPermissionsPage'))(action.payload)(state);

        case 'admin/GET_ROLE_PERMISSIONS_SUCCESS':
            return O.set(adminStateO.prop('rolePermissionsPage'))(action.payload)(state);

        case 'admin/GET_ROLE_SUCCESS':
            return O.set(adminStateO.prop('roleDetail'))(opt(action.payload))(state);

        case 'admin/GET_USERS_SUCCESS':
            return O.set(adminStateO.prop('usersPage'))(action.payload)(state);

        case 'admin/GET_USER_SUCCESS':
            return O.set(adminStateO.prop('userDetail'))(opt(action.payload))(state);

        case 'admin/GET_MEMBER_OF_SUBJECTS_SUCCESS':
            return O.set(adminStateO.prop('memberOfSubjects'))(action.payload)(state);

        case 'admin/GET_USER_CONTACTS_SUCCESS':
            return O.set(adminStateO.prop('userContacts'))(action.payload)(state);

        case 'admin/GET_USER_ASSOCIATIONS_SUCCESS':
            return O.set(adminStateO.prop('userAssociations'))(action.payload)(state);

        case 'admin/GET_USER_OTHER_SUBJECT_ROLES_SUCCESS':
            return O.set(adminStateO.prop('userOtherSubjectRoles'))(action.payload)(state);

        case 'admin/GET_USER_BREEDERS_SUCCESS':
            return O.set(adminStateO.prop('userBreeders'))(action.payload)(state);

        case 'admin/GET_ORGANIZATIONS_SUCCESS':
            return O.set(adminStateO.prop('organizations'))(action.payload)(state);

        case 'admin/CLEAR_USER':
            return O.set(adminStateO.prop('userDetail'))(none)(state);

        case 'admin/CLEAR_ROLE':
            return O.set(adminStateO.prop('roleDetail'))(none)(state);

        case 'admin/SET_LOADING': {
            const {id, loading} = action.payload;

            return O.modify(adminStateO.prop('loading'))(setItem(loading)(id))(state);
        }

        case 'admin/GET_MANAGEMENT_BREEDERS_TABLE_DATA_SUCCESS':
            return O.set(adminStateO.prop('managementBreedersPage'))(action.payload)(state);

        case 'admin/SET_BREEDERS_OPTIONS':
            return O.set(adminStateO.prop('managementBreedersOptions'))(action.payload)(state);

        case 'admin/SET_BREEDERS_EXTRA_COLUMNS':
            return O.set(adminStateO.prop('managementBreedersExtraColumns'))(action.payload)(state);

        case 'admin/SET_BREEDERS_FARMS':
            return O.set(adminStateO.prop('managementBreedersFarms'))(action.payload)(state);

        case 'admin/GET_BREEDER_DETAIL_SUCCESS':
            return O.set(adminStateO.prop('managementBreederDetail'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_BREEDERS_USERS':
            return O.set(adminStateO.prop('managementBreedersUsers'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_BREEDERS_ROLES':
            return O.set(adminStateO.prop('managementBreedersRoles'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_SUBJECT_MODAL_VISIBLE':
            return O.set(adminStateO.prop('managementSubjectModalVisible'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ASSOCIATIONS_OPTIONS':
            return O.set(adminStateO.prop('managementAssociationsOptions'))(action.payload)(state);

        case 'admin/GET_MANAGEMENT_ASSOCIATIONS_TABLE_DATA_SUCCESS':
            return O.set(adminStateO.prop('managementAssociationsPage'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ASSOCIATIONS_EXTRA_COLUMNS':
            return O.set(adminStateO.prop('managementAssociationsExtraColumns'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ASSOCIATIONS_USERS':
            return O.set(adminStateO.prop('managementAssociationsUsers'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ASSOCIATIONS_ROLES':
            return O.set(adminStateO.prop('managementAssociationsRoles'))(action.payload)(state);

        case 'admin/GET_MANAGEMENT_ASSOCIATION_DETAIL_SUCCESS':
            return O.set(adminStateO.prop('managementAssociationDetail'))(action.payload)(state);

        case 'admin/SET_AUTH_DISCRIMINATOR_OPTIONS':
            return O.set(adminStateO.prop('adminDiscriminators'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_OTHER_SUBJECTS_OPTIONS':
            return O.set(adminStateO.prop('managementOtherSubjectsOptions'))(action.payload)(state);

        case 'admin/GET_MANAGEMENT_OTHER_SUBJECTS_TABLE_DATA_SUCCESS':
            return O.set(adminStateO.prop('managementOtherSubjectsPage'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_OTHER_SUBJECTS_EXTRA_COLUMNS':
            return O.set(adminStateO.prop('managementOtherSubjectsExtraColumns'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_OTHER_SUBJECTS_USERS':
            return O.set(adminStateO.prop('managementOtherSubjectsUsers'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_OTHER_SUBJECTS_ROLES':
            return O.set(adminStateO.prop('managementOtherSubjectsRoles'))(action.payload)(state);

        case 'admin/GET_MANAGEMENT_OTHER_SUBJECT_DETAIL_SUCCESS':
            return O.set(adminStateO.prop('managementOtherSubjectDetail'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ORGANIZATIONS_OPTIONS':
            return O.set(adminStateO.prop('managementOrganizationsOptions'))(action.payload)(state);

        case 'admin/GET_MANAGEMENT_ORGANIZATIONS_TABLE_DATA_SUCCESS':
            return O.set(adminStateO.prop('managementOrganizationsPage'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ORGANIZATIONS_EXTRA_COLUMNS':
            return O.set(adminStateO.prop('managementOrganizationsExtraColumns'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ORGANIZATIONS_USERS':
            return O.set(adminStateO.prop('managementOrganizationsUsers'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ORGANIZATIONS_ROLES':
            return O.set(adminStateO.prop('managementOrganizationsRoles'))(action.payload)(state);

        case 'admin/GET_MANAGEMENT_ORGANIZATION_DETAIL_SUCCESS':
            return O.set(adminStateO.prop('managementOrganizationDetail'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ORGANIZATION_FARMS':
            return O.set(adminStateO.prop('managementOrganizationFarms'))(action.payload)(state);

        case 'admin/SET_MANAGEMENT_ORGANIZATION_CENTERS':
            return O.set(adminStateO.prop('managementOrganizationCenters'))(action.payload)(state);

        default:
            return state;
    }
};
